import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { DateTime } from 'luxon'

import {
    Popover,
    Select,
    MenuItem,
    Button,
    Typography,
    Box,
    IconButton,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Paper,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Close } from '@material-ui/icons'

import {
    ListVendor,
    Service,
    Unit,
    Area,
    AreaConfig,
    ModelMap,
    IdBoolMap,
    ModelListMap,
    Lease,
    AreaStatusTag,
    isServiceArea,
} from '../../../../models'
import {
    CreateWorkOrderRequest,
    CreateWorkorderActionThunk,
    UnitRes,
} from '../../../../store'
import { useTimezone } from '../../../../contexts/timezone'

interface ServiceAreaUIState {
    [areaId: number]: {
        include: boolean
    }
}

interface CreateWorkorderPopoverProps {
    open: boolean
    anchorEl: HTMLElement | null
    unit: Unit
    service: Service
    vendorList: ListVendor[]
    areaConfigMap: ModelMap<AreaConfig>
    unitMap: Record<number, UnitRes> | undefined
    scheduleId: number
    serviceAreaMap: IdBoolMap
    areaLeaseMap: ModelListMap<Lease>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    onClose: () => void
    onCreateWorkorder: (
        req: CreateWorkOrderRequest,
    ) => CreateWorkorderActionThunk
}

const ServiceArea = (props: {
    area: Area
    areaConfig?: AreaConfig
    checked: boolean
    areaStatusTag?: AreaStatusTag
    onChange: () => void
}) => {
    const { area, checked, onChange, areaConfig, areaStatusTag } = props
    return (
        <FormControlLabel
            control={
                <Checkbox
                    color="primary"
                    checked={checked}
                    onChange={onChange}
                />
            }
            label={
                <div>
                    <span>
                        {areaConfig?.name} {area.area_label}{' '}
                        {areaStatusTag
                            ? `(${areaStatusTag.area_status_config.name})`
                            : ''}
                    </span>
                </div>
            }
        />
    )
}

export const CreateWorkorderPopover: React.FC<CreateWorkorderPopoverProps> = ({
    open,
    anchorEl,
    unit,
    service,
    vendorList,
    areaConfigMap,
    unitMap,
    serviceAreaMap,
    areaLeaseMap,
    areaStatusTagMap,
    onClose,
    onCreateWorkorder,
    scheduleId,
}) => {
    const timezoneState = useTimezone()
    const [selectedVendor, setSelectedVendor] = useState('')
    const [startDate, setStartDate] = useState<DateTime | null>(null)
    const [endDate, setEndDate] = useState<DateTime | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [
        serviceAreaUIState,
        setServiceAreaUIState,
    ] = useState<ServiceAreaUIState>({})

    // Get unit areas from unitMap, filtered by shows_on_schedule
    const unitAreas = (unitMap?.[unit.id]?.areas || []).filter(
        (area) => areaConfigMap[area.area_config]?.shows_on_schedule,
    )

    // Initialize service area state when component mounts
    React.useEffect(() => {
        if (unitAreas.length > 0) {
            const newSaUiState: ServiceAreaUIState = {}
            unitAreas.forEach((area) => {
                const shouldBeChecked = isServiceArea(
                    area,
                    areaLeaseMap,
                    false,
                    serviceAreaMap[area.id] ?? true,
                    areaStatusTagMap,
                    areaConfigMap[area.area_config],
                )
                newSaUiState[area.id] = {
                    include: shouldBeChecked,
                }
            })
            setServiceAreaUIState(newSaUiState)
        }
    }, [unitAreas])

    const handleSubmit = async () => {
        if (!selectedVendor || !startDate || !endDate) {
            return
        }

        // Additional validation: ensure end date is not before start date
        if (endDate < startDate) {
            toast.error('End date cannot be before start date')
            return
        }

        setIsSubmitting(true)
        try {
            // Get selected service areas
            const serviceAreas = Object.keys(serviceAreaUIState).reduce<
                number[]
            >((prev, areaId) => {
                const aid = parseInt(areaId)
                if (serviceAreaUIState[aid].include) {
                    return prev.concat(aid)
                }
                return prev
            }, [])

            const createReq: CreateWorkOrderRequest = {
                body: {
                    units: [{ unit_id: unit.id, service_areas: serviceAreas }],
                    service: service.id,
                    vendor: Number(selectedVendor),
                    start_date: startDate.toISO() ?? '',
                    end_date: endDate.toISO() ?? '',
                    schedule: scheduleId,
                },
            }

            await onCreateWorkorder(createReq)
            toast.success('Workorder created successfully')
            onClose()
        } catch (error) {
            console.error('Failed to create workorder:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setSelectedVendor('')
        setStartDate(null)
        setEndDate(null)
        setServiceAreaUIState({})
        onClose()
    }

    const availableVendors = vendorList.filter((vendor) =>
        vendor.services.some(
            (vendorService) => vendorService.id === service.id,
        ),
    )

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                style: {
                    padding: '24px',
                    minWidth: '400px',
                    maxWidth: '500px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: '20px' }}
            >
                <Typography
                    variant="h6"
                    style={{ fontWeight: 600, color: '#1976d2' }}
                >
                    Create Workorder
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    style={{ color: '#666' }}
                >
                    <Close />
                </IconButton>
            </Box>

            {/* Unit and Service Info */}
            <Box
                style={{
                    marginBottom: '20px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                }}
            >
                <Typography
                    variant="body2"
                    style={{ marginBottom: '4px', fontWeight: 500 }}
                >
                    <span style={{ color: '#495057' }}>Unit:</span> {unit.name}
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                    <span style={{ color: '#495057' }}>Service:</span>{' '}
                    {service.name}
                </Typography>
            </Box>

            {/* Vendor Selection */}
            <Box style={{ marginBottom: '20px' }}>
                <Typography
                    variant="body2"
                    style={{ marginBottom: '8px', fontWeight: 500 }}
                >
                    Select Vendor:
                </Typography>

                {availableVendors.length === 0 ? (
                    <div
                        style={{
                            padding: '12px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '8px',
                            color: '#856404',
                            fontSize: '14px',
                        }}
                    >
                        No vendors available for {service.name} service.
                    </div>
                ) : (
                    <Select
                        fullWidth
                        value={selectedVendor}
                        onChange={(e) =>
                            setSelectedVendor(e.target.value as string)
                        }
                        variant="outlined"
                        style={{ marginTop: '4px' }}
                    >
                        <MenuItem value="">
                            <em style={{ color: '#999' }}>Select a vendor</em>
                        </MenuItem>
                        {availableVendors.map((vendor) => (
                            <MenuItem key={vendor.id} value={vendor.id}>
                                {vendor.name}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>

            {/* Date Fields - Side by Side */}
            <Box style={{ marginBottom: '16px' }}>
                <Typography
                    variant="body2"
                    style={{ marginBottom: '12px', fontWeight: 500 }}
                >
                    Schedule Dates:
                </Typography>
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                    }}
                >
                    <KeyboardDatePicker
                        value={
                            startDate?.setZone(undefined, {
                                keepLocalTime: true,
                            }) || null
                        }
                        onChange={(date) => {
                            if (date === null) {
                                return
                            } else {
                                const newStartDate = DateTime.fromJSDate(date)
                                    .setZone(timezoneState.currentTimezone, {
                                        keepLocalTime: true,
                                    })
                                    .startOf('day')
                                setStartDate(newStartDate)

                                // If end date is before the new start date, clear it
                                if (endDate && endDate < newStartDate) {
                                    setEndDate(null)
                                }
                            }
                        }}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="Start Date"
                        style={{ width: '100%' }}
                        minDate={new Date()} // Prevent selecting past dates
                    />
                    <KeyboardDatePicker
                        value={
                            endDate?.setZone(undefined, {
                                keepLocalTime: true,
                            }) || null
                        }
                        onChange={(date) => {
                            if (date === null) {
                                return
                            } else {
                                const newEndDate = DateTime.fromJSDate(date)
                                    .setZone(timezoneState.currentTimezone, {
                                        keepLocalTime: true,
                                    })
                                    .endOf('day')

                                // Only set end date if it's not before start date
                                if (
                                    !startDate ||
                                    newEndDate >= startDate.startOf('day')
                                ) {
                                    setEndDate(newEndDate)
                                } else {
                                    // Show error message if end date is before start date
                                    toast.error(
                                        'End date cannot be before start date',
                                    )
                                }
                            }
                        }}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="End Date"
                        style={{ width: '100%' }}
                        minDate={startDate ? startDate.toJSDate() : new Date()} // Set minimum date to start date
                    />
                </Box>
            </Box>

            {/* Service Areas Section */}
            {unitAreas.length > 0 && (
                <Box style={{ marginBottom: '24px' }}>
                    <Typography
                        variant="body2"
                        style={{ marginBottom: '8px', fontWeight: 500 }}
                    >
                        Service Areas:
                    </Typography>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginBottom: '12px', display: 'block' }}
                    >
                        Select areas to include in this workorder
                    </Typography>

                    <Paper
                        elevation={0}
                        style={{
                            padding: '16px',
                            maxHeight: '180px',
                            overflow: 'auto',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                        }}
                    >
                        <FormGroup>
                            <Box
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr',
                                    gap: '6px',
                                }}
                            >
                                {unitAreas.map((area) => (
                                    <ServiceArea
                                        key={`SERVICE_AREA_${area.id}`}
                                        area={area}
                                        areaConfig={
                                            areaConfigMap[area.area_config]
                                        }
                                        checked={
                                            serviceAreaUIState[area.id]
                                                ?.include === true
                                        }
                                        onChange={() =>
                                            setServiceAreaUIState({
                                                ...serviceAreaUIState,
                                                [area.id]: {
                                                    include: !serviceAreaUIState[
                                                        area.id
                                                    ]?.include,
                                                },
                                            })
                                        }
                                        areaStatusTag={
                                            areaStatusTagMap[area.id]
                                        }
                                    />
                                ))}
                            </Box>
                        </FormGroup>
                    </Paper>
                </Box>
            )}

            {/* Action Buttons */}
            <Box
                display="flex"
                style={{
                    gap: '12px',
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    style={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                        isSubmitting ||
                        !selectedVendor ||
                        !startDate ||
                        !endDate ||
                        endDate < startDate ||
                        availableVendors.length === 0
                    }
                    style={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: '8px 24px',
                    }}
                >
                    {isSubmitting ? 'Creating...' : 'Create Workorder'}
                </Button>
            </Box>
        </Popover>
    )
}
